import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import {
  createGranularStep as createGranularStepService,
  updateGranularStep as updateGranularStepService,
  deleteGranularStep as deleteGranularStepService,
  getAllGranularSteps as getAllGranularStepsService,
  createWorkedExampleForGranularStep as createWorkedExampleService,
  updateWorkedExampleForGranularStep as updateWorkedExampleService,
  deleteWorkedExampleForGranularStep as deleteWorkedExampleService,
} from "../../services/developmental-rubrics/granular-steps.service.js";

import {
  updateDevelopmentalRubricFormField,
} from "../../store/actions/developmental-rubrics.actions.js";
import { handlerErrorV2 } from "../../utils/errors.jsx";
import { toggleAlertInfo, toggleAlertModule } from "../../store/actions/alert.actions";
import MarkingUtils from "../../utils/marking.utils.js";

const useGranularSteps = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const lcs = useSelector((state) => state?.dr?.form?.lcs || []);
  const currentCurriculum = useSelector(
    (state) => state?.assessment?.currentCurriculum || {}
  );

  const updateForm = async (curriculumId, drId, lcId) => {
    const response = await getAllGranularStepsService(curriculumId, drId, lcId);

    if (!response.data && response.error) {
      return {
        error: true,
        message: response.error.message || "An error ocurred",
      };
    }

    const data = response.data;

    const newLcs = lcs.map((item) => {
      if (item._id === lcId) {
        return { ...item, granularSteps: MarkingUtils.sortGranularSteps(currentCurriculum?.levels, data) };
      }
      return item;
    })
    dispatch(updateDevelopmentalRubricFormField("lcs", newLcs));
  };
  
  const updateFormGS = (type, lcId, gsData) => {
    let newLcs = lcs;
    if (type === 'create') {
      newLcs = newLcs.map((item) => {
        if (item._id === lcId) {
          return { ...item, granularSteps: [...item.granularSteps, gsData] };
        }
        return item;
      })
    } else if (type === 'update') {
      const { gsId } = gsData;
      newLcs = newLcs.map((item) => {
        if (item._id === lcId) {
          const newGs = item.granularSteps.map((gs) => {
            if (gs.id === gsId) {
              return gsData;
            }
            return gs;
          })
          return { ...item, granularSteps: newGs };
        }
    
        return item;
      })
    } else if (type === 'delete') {
      const { gsId } = gsData;
      newLcs = newLcs.map((item) => {
        if (item._id === lcId) {
          return { ...item, granularSteps: item.granularSteps.filter((gs) => gs.id !== gsId) };
        }
        return item;
      })
    }

    newLcs = newLcs.map((item) => {
      if (item._id === lcId) {
        return { ...item, granularSteps: MarkingUtils.sortGranularSteps(currentCurriculum?.levels, item.granularSteps) };
      }
      return item;
    })
    dispatch(updateDevelopmentalRubricFormField("lcs", newLcs));
  }

  const createGranularStep = async (curriculumId, drId = 'created', lcId, form = {}) => {
    if (form.definition === "") {
      return {
        error: true,
        message: "Definition is required",
      };
    }
    if (drId === 'created') {
      updateFormGS('create', lcId, form)
      return {
        error: false
      }
    } 
  
    try {
      dispatch(
        toggleAlertInfo(
          true,
          "Inserting granular step",
          "Please wait as this may take a few minutes",
          "loading"
        )
      );
      if (form.definition === "") {
        return {
          error: true,
          message: "Definition is required",
        };
      }
      const data = form;
      const response = await createGranularStepService(
        curriculumId,
        drId,
        lcId,
        data
      );
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || "An error ocurred",
        };
      } else {
        await updateForm(curriculumId, drId, lcId);
        enqueueSnackbar("Developmental rubric updated successfully", {
          variant: "success",
        });
        dispatch(toggleAlertModule());
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      dispatch(toggleAlertInfo(false));
    }
  };

  const updateGranularStep = async (curriculumId, drId = 'created', lcId, gsId, form = {}) => {
    if (form.definition === "") {
      return {
        error: true,
        message: "Definition is required",
      };
    }
    if (drId === 'created') {
      updateFormGS('update', lcId, form)
      return {
        error: false
      }
    } 

    try {
      dispatch(
        toggleAlertInfo(
          true,
          "Updating granular step",
          "Please wait as this may take a few minutes",
          "loading"
        )
      );
      const data = form;
      const response = await updateGranularStepService(
        curriculumId,
        drId,
        lcId,
        gsId,
        data
      );
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || "An error ocurred",
        };
      } else {
        await updateForm(curriculumId, drId, lcId);
        enqueueSnackbar("Developmental rubric updated successfully", {
          variant: "success",
        });
        dispatch(toggleAlertModule());

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      dispatch(toggleAlertInfo(false));
    }
  };

  const deleteGranularStep = async (curriculumId, drId = 'created', lcId, gsId) => {
    if (drId === 'created') {
      updateFormGS('delete', lcId, { gsId })
      return {
        error: false
      }
    } 

    setIsLoading(true);
    try {
      dispatch(
        toggleAlertInfo(
          true,
          "Removing granular step",
          "Please wait as this may take a few minutes",
          "loading"
        )
      );
      const response = await deleteGranularStepService(
        curriculumId,
        drId,
        lcId,
        gsId
      );
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || "An error ocurred",
        };
      } else {
        await updateForm(curriculumId, drId, lcId);
        enqueueSnackbar("Developmental rubric updated successfully", {
          variant: "success",
        });

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      dispatch(toggleAlertInfo(false));
      setIsLoading(false);
    }
  };

  const formData = (form) => {
    return {
      comment: form.comment,
      media: form.media
    }
  };
  const createWorkedExampleForGranularStep = async (curriculumId, drId, lcId, gsId, form = {}) => {
    try {
      const data = formData(form);
      const response = await createWorkedExampleService(
        curriculumId,
        drId,
        lcId,
        gsId,
        data
      );
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || "An error ocurred",
        };
      } else {
        await updateForm(curriculumId, drId, lcId);
        enqueueSnackbar("Developmental rubric updated successfully", {
          variant: "success",
        });
        dispatch(toggleAlertModule());

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const updateWorkedExampleForGranularStep = async (curriculumId, drId = 'created', lcId, gsId, weId, form = {}) => {
    try {
      const data = formData(form);
      const response = await updateWorkedExampleService(
        curriculumId,
        drId,
        lcId,
        gsId,
        weId,
        data
      );
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || "An error ocurred",
        };
      } else {
        await updateForm(curriculumId, drId, lcId);
        enqueueSnackbar("Developmental rubric updated successfully", {
          variant: "success",
        });
        dispatch(toggleAlertModule());

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const deleteWorkedExampleForGranularStep = async (curriculumId, drId = 'created', lcId, gsId, weId) => {
    setIsLoading(true);
    try {
      const response = await deleteWorkedExampleService(
        curriculumId,
        drId,
        lcId,
        gsId,
        weId
      );
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || "An error ocurred",
        };
      } else {
        await updateForm(curriculumId, drId, lcId);
        enqueueSnackbar("Developmental rubric updated successfully", {
          variant: "success",
        });

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    createGranularStep,
    updateGranularStep,
    deleteGranularStep,
    createWorkedExampleForGranularStep,
    updateWorkedExampleForGranularStep,
    deleteWorkedExampleForGranularStep
  };
};

export default useGranularSteps;
